import { APP_SETTINGS } from '../config/appSettings'
import type { SyncCycleResult, SyncRequest } from './syncRuntime'

interface SyncTabCoordinatorOptions {
  accountKey: string
  onLeadershipChange: (leader: boolean) => void
  onRequest: (request: SyncRequest, delayMs?: number) => void
  onPeerResult: (result: SyncCycleResult) => void
  isVisible?: () => boolean
  visibilityTarget?: Pick<Document, 'addEventListener' | 'removeEventListener'>
}

type SyncTabMessage =
  | { type: 'request'; request: SyncRequest; delayMs?: number }
  | { type: 'result'; result: SyncCycleResult }

export class SyncTabCoordinator {
  private channel: BroadcastChannel | null = null
  private retryTimer: number | null = null
  private releaseLeadership: (() => void) | null = null
  private disposed = false
  private leader = false
  private readonly isVisible: () => boolean
  private readonly visibilityTarget: Pick<Document, 'addEventListener' | 'removeEventListener'> | null

  constructor(private readonly options: SyncTabCoordinatorOptions) {
    this.isVisible = options.isVisible
      ?? (() => typeof document === 'undefined' || document.visibilityState === 'visible')
    this.visibilityTarget = options.visibilityTarget
      ?? (typeof document === 'undefined' ? null : document)
  }

  start(): void {
    if (typeof BroadcastChannel === 'undefined' || !navigator.locks) {
      this.setLeader(true)
      return
    }
    this.channel = new BroadcastChannel(`minimany-sync:${this.options.accountKey}`)
    this.channel.onmessage = (event: MessageEvent<SyncTabMessage>) => {
      const message = event.data
      if (!message || typeof message !== 'object' || !('type' in message)) return
      if (message.type === 'request' && this.leader) {
        this.options.onRequest(message.request, message.delayMs)
      }
      if (message.type === 'result' && !this.leader) this.options.onPeerResult(message.result)
    }
    this.visibilityTarget?.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.tryLeadership()
  }

  request(request: SyncRequest, delayMs?: number): void {
    if (this.leader) this.options.onRequest(request, delayMs)
    else this.channel?.postMessage({ type: 'request', request, delayMs } satisfies SyncTabMessage)
  }

  publishResult(result: SyncCycleResult): void {
    if (!this.leader) return
    this.channel?.postMessage({ type: 'result', result } satisfies SyncTabMessage)
  }

  dispose(): void {
    this.disposed = true
    this.releaseLeadership?.()
    if (this.retryTimer !== null) globalThis.clearTimeout(this.retryTimer)
    this.visibilityTarget?.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.channel?.close()
    this.setLeader(false)
  }

  private tryLeadership(): void {
    if (this.disposed || this.leader || !navigator.locks || !this.isVisible()) return
    void navigator.locks.request(
      `minimany-sync-leader:${this.options.accountKey}`,
      { ifAvailable: true },
      async (lock) => {
        if (!lock || this.disposed || !this.isVisible()) return
        this.setLeader(true)
        await new Promise<void>((resolve) => { this.releaseLeadership = resolve })
        this.releaseLeadership = null
        this.setLeader(false)
      },
    ).catch(() => {
      // Preserve correctness when a browser exposes but cannot use Web Locks.
      if (this.isVisible()) this.setLeader(true)
    }).finally(() => {
      if (this.disposed) return
      this.retryTimer = globalThis.setTimeout(
        () => {
          this.retryTimer = null
          this.tryLeadership()
        },
        APP_SETTINGS.sync.leaderElectionRetryMs,
      )
    })
  }

  private readonly handleVisibilityChange = (): void => {
    if (!this.isVisible()) {
      if (this.retryTimer !== null) {
        globalThis.clearTimeout(this.retryTimer)
        this.retryTimer = null
      }
      this.releaseLeadership?.()
      this.setLeader(false)
      return
    }
    if (this.retryTimer !== null) {
      globalThis.clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
    this.tryLeadership()
  }

  private setLeader(leader: boolean): void {
    if (this.leader === leader) return
    this.leader = leader
    this.options.onLeadershipChange(leader)
  }
}
